import type { AckPayloads, CoverageReport, KnowledgeBase, OwnerView } from '@ralph/contracts'
import { ACKS } from '@ralph/contracts'
import clsx from 'clsx'
import { ArrowUpRight, Check, Database, Info, Route, TriangleAlert } from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { AskManagers, type AskState } from '@/components/marketplace/consumer/AskManagers.tsx'
import { ownerName } from '@/components/marketplace/consumer/agent-identity.ts'
import { BuilderSection, Note, SECTION_IDS } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import {
  type AttachableRow,
  attachableRows,
  type CoverageEntry,
  type CoverageLine,
  formatSpace,
  missingSentence,
  peopleBeyond,
  presentCoverage,
  unlistedAttachments
} from '@/components/marketplace/maker/knowledge-coverage.ts'
import { Dialog, DialogBody, DialogFooter } from '@/components/ui/Dialog.tsx'
import { Button, Spinner } from '@/components/ui/primitives.tsx'
import { useKnowledgeBases, useLendToAgent } from '@/hooks/use-knowledge-bases.ts'
import { describeApiError, useI18n } from '@/i18n/index.ts'

/**
 * Knowledge: the bases this agent answers from (PRD-26 D-16, D-17, D-43, D-46).
 *
 * The builder attaches and asks; the Knowledge page maintains. So this step lists what the maker
 * can use — *You maintain*, then *You can use*, the Knowledge page's own words — with a way to each
 * base's page, and nothing else: no inline manager, no "Select all" (attaching every base someone
 * can use is never a decision, it is a default nobody made).
 *
 * The organising idea is that **attaching is not sharing**. Every attached base says, for the
 * audience being chosen in step 7, whether everyone it reaches gets answers from it, whether it
 * answers through this agent for everyone (and who allowed that), or who misses out — with the one
 * thing the maker can do about it: allow it themselves when they maintain the base, or ask its
 * managers. A base attached a moment ago has no reading until it is saved, and says so.
 *
 * The sub-blocks are plain `<div>`s under an eyebrow rather than `<section>`s: a named `<section>`
 * would expose a second `role=region` matching "…Knowledge…", and the browser suite reaches this
 * step through `getByRole('region', { name: 'Knowledge' })`.
 */
export const KnowledgeSection = ({
  step,
  agentId,
  agentName,
  owner,
  acts,
  draft,
  savedIds,
  coverage,
  disabled,
  onChange
}: {
  step: number
  agentId: string
  agentName: string
  /** Who maintains the agent — part of what a base's maintainers read before allowing it. */
  owner: OwnerView
  /** Whether it can take actions, which the same decision states. */
  acts: boolean
  draft: BuilderDraft
  /** What is saved: coverage exists only for these. */
  savedIds: readonly string[]
  coverage: { data: CoverageReport | undefined; isLoading: boolean; isFetching: boolean; isError: boolean }
  disabled: boolean
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  const i18n = useI18n()
  // Read in the body, never at module scope: `const strings = makerStrings.sections.knowledge`
  // freezes whichever language was live when the bundle evaluated (PRD-27 F-3). The three inner
  // components below each read their own for the same reason.
  const strings = i18n.t.marketplace.maker.sections.knowledge
  const maintain = useKnowledgeBases('maintain')
  const use = useKnowledgeBases('use')
  const [lending, setLending] = useState<CoverageEntry | null>(null)

  const rows = attachableRows({ maintain: maintain.data?.items ?? [], use: use.data?.items ?? [] })
  const attached = new Set(draft.knowledgeBaseIds)
  const saved = new Set(savedIds)
  const entries = coverage.data?.bases ?? []
  const entryById = new Map(entries.map((entry) => [entry.baseId, entry]))
  const unlisted = unlistedAttachments(draft.knowledgeBaseIds, rows, entries)

  const lineFor = (id: string): CoverageLine | null => {
    if (!attached.has(id)) return null
    // Saved but still being measured — a first read, or the re-read after a save — is the
    // spinner's business below, not "save to see".
    if (saved.has(id) && !entryById.has(id) && (coverage.isLoading || coverage.isFetching)) return null
    return presentCoverage(saved.has(id) ? entryById.get(id) : undefined)
  }

  const toggle = (id: string, on: boolean) =>
    onChange({
      knowledgeBaseIds: on ? [...draft.knowledgeBaseIds, id] : draft.knowledgeBaseIds.filter((value) => value !== id)
    })

  const loading = maintain.isLoading || use.isLoading
  const failed = maintain.isError || use.isError
  const nothingToAttach = !loading && !failed && rows.maintain.length === 0 && rows.use.length === 0

  const renderRow = (row: AttachableRow, showOwner: boolean) => (
    <li key={row.id}>
      <BaseRow
        row={row}
        showOwner={showOwner}
        attached={attached.has(row.id)}
        disabled={disabled}
        onToggle={(on) => toggle(row.id, on)}
      >
        <CoverageFor
          line={lineFor(row.id)}
          entry={entryById.get(row.id)}
          agentId={agentId}
          agentName={agentName}
          baseName={row.name}
          disabled={disabled}
          onLend={setLending}
        />
      </BaseRow>
    </li>
  )

  return (
    <BuilderSection
      id={SECTION_IDS.knowledge}
      step={step}
      title={strings.title}
      lead={strings.lead}
      aside={
        <Link
          to="/knowledge"
          title={strings.manageHint}
          className="readout inline-flex h-[28px] shrink-0 items-center gap-1.5 rounded-full border border-rule px-2.5 text-[10px] tracking-[0.05em] text-fg-muted transition-colors hover:border-rule-strong hover:text-fg"
        >
          {strings.manage}
          <ArrowUpRight size={11} strokeWidth={2.2} aria-hidden />
        </Link>
      }
    >
      {loading && (
        <p className="readout flex items-center gap-2 text-fg-subtle">
          <Spinner size={12} />
          {strings.loading}
        </p>
      )}
      {failed && (
        <Note tone="warn" icon={<Info size={13} strokeWidth={2.1} />}>
          {strings.failed}
        </Note>
      )}

      {nothingToAttach && (
        <div className="rounded-tile border border-dashed border-rule-strong px-3.5 py-3">
          <p className="text-[13px]">{strings.useEmpty}</p>
          <p className="mt-1 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{strings.emptyHint}</p>
        </div>
      )}

      {!nothingToAttach && !loading && (
        <>
          <Group title={strings.maintainTitle} empty={maintain.isSuccess ? strings.maintainEmpty : null}>
            {rows.maintain.map((row) => renderRow(row, row.owner.kind === 'group'))}
          </Group>
          <Group title={strings.useTitle} empty={use.isSuccess ? strings.useEmpty : null}>
            {rows.use.map((row) => renderRow(row, true))}
          </Group>
        </>
      )}

      {unlisted.length > 0 && (
        <Group title={strings.otherTitle} lead={strings.otherLead} empty={null}>
          {unlisted.map((base) => (
            <li key={base.id}>
              <BaseRow
                row={{
                  id: base.id,
                  name: base.name ?? strings.unknownBase,
                  owner: base.owner,
                  documentCount: null,
                  storageBytes: null,
                  status: null
                }}
                showOwner
                linked={false}
                attached
                disabled={disabled}
                onToggle={(on) => toggle(base.id, on)}
              >
                <CoverageFor
                  line={lineFor(base.id)}
                  entry={entryById.get(base.id)}
                  agentId={agentId}
                  agentName={agentName}
                  baseName={base.name ?? strings.unknownBase}
                  disabled={disabled}
                  onLend={setLending}
                />
              </BaseRow>
            </li>
          ))}
        </Group>
      )}

      <p className="readout text-fg-muted">{strings.attachedCount(i18n, draft.knowledgeBaseIds.length)}</p>

      {draft.knowledgeBaseIds.length > 0 && (
        <p className="flex items-center gap-1.5 text-[12px] text-fg-subtle">
          {coverage.isFetching ? <Spinner size={11} /> : <Info size={12} strokeWidth={2.1} aria-hidden />}
          {coverage.isFetching ? strings.coverageLoading : strings.checkedAgainst}
        </p>
      )}
      {coverage.isError && (
        <Note tone="info" icon={<Info size={12} strokeWidth={2.2} />}>
          {strings.coverageFailed}
        </Note>
      )}

      {lending && (
        <LendDialog
          entry={lending}
          agentId={agentId}
          agentName={agentName}
          owner={owner}
          acts={acts}
          reach={coverage.data?.reach ?? 0}
          onClose={() => setLending(null)}
        />
      )}
    </BuilderSection>
  )
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

const Group = ({
  title,
  lead,
  empty,
  children
}: {
  title: string
  lead?: string
  /** Shown when the group has no rows; `null` while the list has not answered. */
  empty: string | null
  children: ReactNode[]
}) => (
  <div>
    <header className="flex items-center gap-2.5 pb-2">
      <h3 className="eyebrow">{title}</h3>
      <span aria-hidden className="h-px flex-1 bg-rule" />
    </header>
    {lead && <p className="mb-2 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">{lead}</p>}
    {children.length === 0 ? (
      empty && <p className="text-[12.5px] text-fg-subtle">{empty}</p>
    ) : (
      <ul className="space-y-2">{children}</ul>
    )}
  </div>
)

type RowShape = Omit<AttachableRow, 'owner' | 'documentCount' | 'storageBytes' | 'status'> & {
  owner: OwnerView | null
  documentCount: number | null
  storageBytes: number | null
  status: KnowledgeBase['status'] | null
}

/**
 * One base: the attach checkbox and what it is, a way to its page on the Knowledge page, and —
 * once attached — what the people this agent reaches get from it.
 */
const BaseRow = ({
  row,
  showOwner,
  linked = true,
  attached,
  disabled,
  onToggle,
  children
}: {
  row: RowShape
  showOwner: boolean
  linked?: boolean
  attached: boolean
  disabled: boolean
  onToggle: (on: boolean) => void
  children: ReactNode
}) => {
  const i18n = useI18n()
  const maker = i18n.t.marketplace.maker
  const strings = maker.sections.knowledge
  const checkboxId = useId()
  const meta = [
    row.documentCount === null ? null : strings.documents(i18n, row.documentCount),
    row.documentCount && row.storageBytes !== null
      ? strings.baseSpace(formatSpace(maker, i18n, row.storageBytes))
      : null,
    row.status === null ? null : strings.state[row.status]
  ].filter((part): part is string => part !== null)

  return (
    <div
      className={clsx(
        'rounded-tile border px-3 py-2.5 transition-[background-color,border-color,box-shadow] duration-200',
        attached
          ? 'border-rule-strong bg-card-solid shadow-glass'
          : 'border-rule bg-card-solid/55 hover:border-rule-strong'
      )}
    >
      <div className="flex items-start gap-2.5">
        <input
          id={checkboxId}
          type="checkbox"
          checked={attached}
          disabled={disabled}
          onChange={(event) => onToggle(event.target.checked)}
          className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
        />
        <label htmlFor={checkboxId} className="min-w-0 flex-1 cursor-pointer">
          <span className="flex min-w-0 items-center gap-2">
            <Database size={13} strokeWidth={2} aria-hidden className="shrink-0 text-fg-subtle" />
            <span className="min-w-0 truncate text-[13.5px] font-semibold tracking-[-0.014em]">{row.name}</span>
          </span>
          <span className="readout mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 pl-[21px] text-fg-subtle">
            {showOwner && row.owner && (
              <span className="inline-flex min-w-0 items-center gap-1">
                {row.owner.kind === 'group' && <GroupAvatar group={row.owner.group} size={16} />}
                <span className="truncate">{strings.ownedBy(ownerName(row.owner))}</span>
              </span>
            )}
            {showOwner && row.owner && meta.length > 0 && <span aria-hidden>·</span>}
            {meta.length > 0 && <span>{meta.join(' · ')}</span>}
          </span>
        </label>
        {linked && (
          <Link
            to={`/knowledge/${encodeURIComponent(row.id)}`}
            aria-label={strings.open(row.name)}
            title={strings.open(row.name)}
            className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full border border-rule text-fg-subtle transition-colors hover:border-rule-strong hover:text-fg"
          >
            <ArrowUpRight size={12} strokeWidth={2.2} aria-hidden />
          </Link>
        )}
      </div>
      {attached && <div className="pl-[25px]">{children}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Coverage, per attached base
// ---------------------------------------------------------------------------

const askStateOf = (line: Extract<CoverageLine, { kind: 'gap' }>): AskState => {
  const action = line.action
  if (action.kind === 'pending') return { kind: 'pending', at: action.at }
  if (action.kind === 'declined') {
    return { kind: 'declined', at: action.at, by: action.by, note: action.note, canAskAgain: action.canAskAgain }
  }
  return { kind: 'none' }
}

const CoverageFor = ({
  line,
  entry,
  agentId,
  agentName,
  baseName,
  disabled,
  onLend
}: {
  line: CoverageLine | null
  entry: CoverageEntry | undefined
  agentId: string
  agentName: string
  baseName: string
  disabled: boolean
  onLend: (entry: CoverageEntry) => void
}) => {
  const i18n = useI18n()
  const strings = i18n.t.marketplace.maker.sections.knowledge
  const lend = i18n.t.marketplace.publish.lend
  if (line === null) return null

  if (line.kind === 'unsaved') {
    return <Fact icon={<Info size={12} strokeWidth={2.2} className="text-fg-subtle" />}>{strings.unsaved}</Fact>
  }
  if (line.kind === 'covered') {
    return (
      <Fact icon={<Check size={12} strokeWidth={2.4} className="text-grounded" />}>{strings.coverage.covered}</Fact>
    )
  }
  if (line.kind === 'lent') {
    return (
      <Fact icon={<Route size={12} strokeWidth={2.2} className="text-grounded" />}>
        {strings.coverage.lent(line.by, line.byAdministrator)}
      </Fact>
    )
  }

  return (
    <div className="mt-2 rounded-tile border border-alert/35 bg-alert-soft px-2.5 py-2">
      <p className="flex items-start gap-1.5 text-pretty text-[12px] leading-[1.45] text-alert">
        <TriangleAlert size={12} strokeWidth={2.2} className="mt-[2px] shrink-0" aria-hidden />
        <span className="min-w-0">{missingSentence(i18n.t.marketplace.maker, i18n, line.missing)}</span>
      </p>
      {!disabled && entry && line.action.kind === 'lend' && (
        <Button
          size="sm"
          className="mt-2"
          icon={<Route size={12} strokeWidth={2.2} aria-hidden />}
          onClick={() => onLend(entry)}
        >
          {lend.action(baseName)}
        </Button>
      )}
      {!disabled && entry && line.action.kind !== 'lend' && (
        <AskManagers
          baseId={entry.baseId}
          baseName={baseName}
          agentId={agentId}
          agentName={agentName}
          ask={askStateOf(line)}
          noMaintainer={line.action.noMaintainer}
        />
      )}
    </div>
  )
}

const Fact = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <p className="mt-2 flex items-start gap-1.5 text-pretty text-[12px] leading-[1.45] text-fg-muted">
    <span className="mt-[2px] shrink-0" aria-hidden>
      {icon}
    </span>
    <span className="min-w-0">{children}</span>
  </p>
)

// ---------------------------------------------------------------------------
// Letting it answer from a base the maker maintains (D-16)
// ---------------------------------------------------------------------------

/**
 * The decision screen. Its sentence is stored verbatim as the record of what was allowed, and the
 * checkbox starts empty: allowing a base to answer for people who cannot open it is the one act in
 * this step that widens anything, and it is its maintainers' to take on purpose.
 */
const LendDialog = ({
  entry,
  agentId,
  agentName,
  owner,
  acts,
  reach,
  onClose
}: {
  entry: CoverageEntry
  agentId: string
  agentName: string
  owner: OwnerView
  acts: boolean
  reach: number
  onClose: () => void
}) => {
  const i18n = useI18n()
  // The consent record is `marketplace.publish`, not `marketplace.maker`: the sentence below is
  // persisted verbatim as the evidence of what this maintainer was told (AGM-042, PRD-27 D-7).
  const t = i18n.t.marketplace.publish.lend
  const titleId = useId()
  const [ack, setAck] = useState(false)
  const lend = useLendToAgent(entry.baseId)

  // The values, and the sentence rendered from them (AGM-042, PRD-27 D-7). The counts inside it
  // are formatted in the language this maintainer actually read it in, and the same values under
  // `ack.lending_from_builder` are what let an auditor read it back in the other one.
  const ackParams: AckPayloads['ack.lending_from_builder'] = {
    agent: agentName,
    base: entry.name,
    ownerKind: owner.kind === 'group' ? 'group' : 'user',
    ownerName: owner.kind === 'group' ? owner.group.name : owner.name,
    reach,
    beyond: peopleBeyond(entry),
    acts
  }
  const sentence = t.sentence(i18n, ackParams)

  return (
    <Dialog
      titleId={titleId}
      icon={<Route size={14} strokeWidth={2.1} />}
      title={t.title(entry.name)}
      lead={t.lead}
      onClose={onClose}
    >
      <DialogBody>
        <p className="rounded-tile border border-rule bg-card px-3.5 py-3 text-pretty text-[13.5px] leading-[1.55]">
          {sentence}
        </p>
        <label className="flex cursor-pointer items-start gap-2.5 rounded-tile border border-rule-strong bg-card px-3.5 py-3">
          <input
            type="checkbox"
            checked={ack}
            onChange={(event) => setAck(event.target.checked)}
            className="mt-[3px] h-[15px] w-[15px] shrink-0 cursor-pointer accent-primary"
          />
          <span className="text-[13.5px] font-semibold tracking-[-0.014em]">{t.ack}</span>
        </label>
        {/*
          One whole sentence, in the reader's language. This line used to be the surface's own
          "that could not be allowed" with the server's English appended after it — a localized
          fragment joined to a foreign one, which is the shape PRD-27 removes, because the two
          halves cannot agree in any language. The refusals worth reading here are the server's
          ("this agent already answers from that knowledge base"), not the generic lead-in's, and
          describeApiError renders them from this reader's catalogue by messageKey (D-4, LOC-033).
        */}
        {lend.isError && (
          <p role="alert" className="text-pretty text-[12.5px] leading-[1.5] text-alert">
            {describeApiError(i18n.t, lend.error)}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          {t.cancel}
        </Button>
        <Button
          variant="primary"
          icon={lend.isPending ? <Spinner size={13} /> : undefined}
          disabled={!ack || lend.isPending}
          onClick={() =>
            lend.mutate(
              {
                agentId,
                ackText: sentence,
                ackKey: ACKS.lendingFromBuilder.key,
                ackVersion: ACKS.lendingFromBuilder.version,
                ackLocale: i18n.locale,
                ackParams
              },
              { onSuccess: onClose }
            )
          }
        >
          {lend.isPending ? t.allowing : t.confirm}
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
